import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ChatIcon from '@mui/icons-material/Chat';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import GroupIcon from '@mui/icons-material/Group';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import StoreIcon from '@mui/icons-material/Store';
import CategoryIcon from '@mui/icons-material/Category';
import StarIcon from '@mui/icons-material/Star';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DomainIcon from '@mui/icons-material/Domain';
import InventoryIcon from '@mui/icons-material/Inventory2';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PublicIcon from '@mui/icons-material/Public';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { type ReactNode } from 'react';
import { useNavModule } from '../../../core/contexts/NavModuleContext';
import { useLineOfBiz } from '../../../core/contexts/LineOfBizContext';
import { TenantSwitcher } from '../TenantSwitcher';
import { LineOfBizSwitcher } from '../LineOfBizSwitcher';
import { ReportTemplateSwitcher } from '../ReportTemplateSwitcher';
import { CustomViewSwitcher } from '../CustomViewSwitcher';
import { ReportsFilterPanel } from '../ReportsFilterPanel';
import { useSelectedReportTemplate } from '../../hooks/useSelectedReportTemplate';

export const SIDEBAR_WIDTH = 240;

/**
 * `to: null` marks the item as a placeholder for a feature whose page
 * hasn't been built yet. It still renders in the sidebar so the
 * navigation reflects the planned IA, but the button is disabled and
 * doesn't navigate anywhere.
 */
interface NavItem {
  label: string;
  to: string | null;
  icon: ReactNode;
  /**
   * Optional content rendered directly underneath the item. Used to slot
   * in contextual selectors (e.g. the report-template picker that lives
   * under the "Report templates" row).
   */
  slot?: ReactNode;
}

/** Inline divider used to break a single group into visual sub-sections. */
interface NavSeparator {
  type: 'separator';
}

type NavEntry = NavItem | NavSeparator;

interface NavSection {
  label: string;
  items: NavEntry[];
}

function isSeparator(entry: NavEntry): entry is NavSeparator {
  return (entry as NavSeparator).type === 'separator';
}

/**
 * Sections inside the tenant block. Order matches the IA spec:
 *   - Active customer    → selector (rendered separately above)
 *   - Customer settings  → settings scoped to the chosen customer
 *   - Active line of biz → selector (rendered separately, placeholder)
 *   - Line of biz settings → everything scoped to the active LOB
 *
 * Items with `to: null` are placeholders (no page yet); they appear in the
 * list as disabled rows so the planned navigation is visible without
 * dead-ending users on a 404.
 */
const customerSettings: NavSection = {
  // Header omitted — the "Active customer" selector above already
  // labels this block.
  label: '',
  items: [
    { label: 'Dashboard', to: '/', icon: <DashboardIcon fontSize="small" /> },
    // Tenant-scoped catalog of organizations + their brand hierarchy.
    // Mirrors the Global Settings → Organizations view but reads /
    // writes through tenant_organization + tenant_brand.
    {
      label: 'Orgs & Brands',
      to: '/tenant-organizations-brands',
      icon: <DomainIcon fontSize="small" />,
    },
    // Product Catalogs — placeholder, route disabled until built.
    // Moved here from the LOB section because catalogs sit one level
    // up: tenant-wide, not LOB-scoped.
    { label: 'Product Catalogs', to: null, icon: <InventoryIcon fontSize="small" /> },
    // Tenant-scoped user management — not built yet; reuse the global
    // /users page or create a tenant-scoped one when needed.
    { label: 'Users', to: null, icon: <PeopleAltIcon fontSize="small" /> },
  ],
};

/**
 * "Line of business settings" — Categories, Personas, Brands & Competitors,
 * Prompt sets, Prompts. Everything scoped to the active LOB but *not*
 * tied to a specific report template.
 */
const lineOfBizSettings: NavSection = {
  // Header omitted by design — the "Active line of business" selector
  // above already labels this block; a second header read as
  // redundant.
  label: '',
  items: [
    { label: 'LOB Dashboard', to: '/line-of-biz-dashboard', icon: <DashboardIcon fontSize="small" /> },
    { label: 'Categories', to: '/line-of-biz-categories', icon: <CategoryIcon fontSize="small" /> },
    { label: 'Personas', to: '/line-of-biz-personas', icon: <GroupIcon fontSize="small" /> },
    // Competitor brands attached to the active LOB
    // (associationType === 'COMPETITOR').
    { label: 'Competitor Brands', to: '/competitor-brands', icon: <StoreIcon fontSize="small" /> },
    { type: 'separator' },
    { label: 'Prompt sets', to: '/prompt-sets', icon: <LayersIcon fontSize="small" /> },
    { label: 'Prompts', to: '/prompts', icon: <ChatIcon fontSize="small" /> },
  ],
};

/**
 * "Report template settings" — its own section, mirroring the LOB block
 * above. The section header + dropdown are rendered inline (so the
 * dropdown sits flush under the title without an extra empty row), then
 * the three sub-rows below target whichever template the dropdown
 * resolves to.
 *
 * The sub-rows render disabled when no template is selected (e.g. the
 * LOB has no templates), so we can't navigate into a subsection with an
 * undefined `:id`.
 */
function buildReportTemplateSubSections(
  selectedReportTemplateId: number | null,
): NavSection {
  const tplPath = (suffix: string) =>
    selectedReportTemplateId != null
      ? `/report-templates/${selectedReportTemplateId}/${suffix}`
      : null;

  // No section label — the inline header above the dropdown owns the
  // title slot. Setting `label: ''` skips the section header in
  // `renderSection` (the overline still renders blank space we don't
  // want here).
  return {
    label: '',
    items: [
      { label: 'Report Settings', to: tplPath('settings'), icon: <SettingsIcon fontSize="small" /> },
      { label: 'Report Prompts', to: tplPath('prompts'), icon: <ChatIcon fontSize="small" /> },
      { label: 'Report Instances', to: tplPath('instances'), icon: <PlayCircleIcon fontSize="small" /> },
      { label: 'Visibility', to: tplPath('visibility'), icon: <VisibilityIcon fontSize="small" /> },
    ],
  };
}

/** Sections shown below the tenant block — system-wide settings & views, unchanged. */
const globalSections: NavSection[] = [
  {
    label: 'Prompts settings',
    items: [
      { label: 'Prompt sets', to: '/prompt-sets', icon: <LayersIcon fontSize="small" /> },
      { label: 'Prompts', to: '/prompts', icon: <ChatIcon fontSize="small" /> },
    ],
  },
  {
    label: 'Global settings',
    items: [
      { label: 'Categories', to: '/categories', icon: <CategoryIcon fontSize="small" /> },
      { label: 'Personas', to: '/personas', icon: <GroupIcon fontSize="small" /> },
      { label: 'Organizations', to: '/organizations', icon: <DomainIcon fontSize="small" /> },
      { label: 'Products', to: '/products', icon: <InventoryIcon fontSize="small" /> },
      { label: 'AI models', to: '/ai-models', icon: <SmartToyIcon fontSize="small" /> },
      { label: 'AI providers', to: '/ai-model-providers', icon: <SmartToyIcon fontSize="small" /> },
      { label: 'AI model routes', to: '/ai-model-ai-providers', icon: <SmartToyIcon fontSize="small" /> },
      { label: 'Funnel stages', to: '/prompt-funnel-stages', icon: <StarIcon fontSize="small" /> },
      { label: 'Visibility weights', to: '/visibility-weights', icon: <VisibilityIcon fontSize="small" /> },
    ],
  },
  {
    label: 'Global views',
    items: [
      { label: 'Customers', to: '/tenants', icon: <ApartmentIcon fontSize="small" /> },
      { label: 'Users', to: '/users', icon: <PeopleAltIcon fontSize="small" /> },
    ],
  },
];

/**
 * Reports Monitoring module — views on the left, then the filter panel
 * that scopes them (customer / line of business / report template).
 */
const reportsSections: NavSection[] = [
  {
    label: 'Views',
    items: [
      { label: 'Report instances', to: '/report-instances', icon: <PublicIcon fontSize="small" /> },
      { label: 'Instance weights', to: '/report-instance-weights', icon: <VisibilityIcon fontSize="small" /> },
    ],
  },
];

export function AppSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Active LOB is shared via context so LOB-scoped pages can filter by it.
  // `LineOfBizContext` resets the selection on tenant switch.
  const { activeLineOfBizId, setActiveLineOfBizId } = useLineOfBiz();

  // Top-level module picked from the AppHeader tabs. The sidebar shows
  // a different set of sections per module — there's no overlap between
  // them and we render only one at a time.
  const { module } = useNavModule();

  // Wrap the LOB setter so user-driven LOB switches land on the LOB
  // Dashboard — the canonical "default view" for a LOB. We only
  // navigate when the *previous* value was non-null, which filters out
  // the two auto-set paths (initial load and tenant switch both
  // transition null → first-LOB and shouldn't yank the user off
  // whatever page they were on). In the Custom Views module the landing
  // is the module's own index — a custom view from the prior LOB isn't
  // reachable under the new scope.
  const handleLineOfBizChange = (next: number | null) => {
    const prev = activeLineOfBizId;
    setActiveLineOfBizId(next);
    if (prev != null && next != null && prev !== next) {
      navigate(module === 'customViews' ? '/custom-views' : '/line-of-biz-dashboard');
    }
  };

  // The three per-template sub-rows (Settings / Prompts / Instances)
  // need a target id even when the user is *not* yet on a template page
  // — otherwise the rows render disabled the entire time they're on
  // Dashboard / Prompts / Categories etc. The hook resolves to the
  // URL-matched id first, falling back to the first template for the
  // active LOB, so the rows light up as soon as any template exists.
  const { effectiveId: selectedReportTemplateId } =
    useSelectedReportTemplate(activeLineOfBizId);

  /**
   * Renders a section. When `disabled` is true, every entry (including
   * navigable ones) renders as a non-clickable row — used by the
   * "Line of business settings" section while no LOB is selected, so
   * users can't drill into LOB-scoped pages without a scope. Placeholder
   * entries are always disabled regardless of this flag.
   */
  const renderSection = (section: NavSection, opts?: { disabled?: boolean }) => (
    <Box key={section.label || 'unlabelled'} sx={{ mb: 1 }}>
      {section.label && (
        <Typography variant="overline" sx={{ px: 2.5, color: 'text.secondary', display: 'block' }}>
          {section.label}
        </Typography>
      )}
      <List dense disablePadding>
        {section.items.map((entry, idx) => {
          if (isSeparator(entry)) {
            return <Divider key={`sep-${section.label}-${idx}`} sx={{ my: 0.75, mx: 2 }} />;
          }

          // Placeholder entries: rendered but not clickable. They use a
          // stable string key (label) since they have no `to`.
          if (entry.to === null) {
            return (
              <Box key={`placeholder:${entry.label}`}>
                <ListItemButton disabled sx={{ mx: 1, borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                    {entry.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={entry.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
                {entry.slot}
              </Box>
            );
          }

          // Section-disabled mode: render the same row visually but
          // without the RouterLink/onClick affordance. Keeps the IA
          // visible (so users see what's available once they pick a LOB)
          // without dead-ending them on pages that haven't been scoped.
          if (opts?.disabled) {
            return (
              <Box key={`disabled:${entry.to}`}>
                <ListItemButton disabled sx={{ mx: 1, borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                    {entry.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={entry.label}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItemButton>
                {entry.slot}
              </Box>
            );
          }

          // Dashboard route is `/` — exact match only, otherwise it would
          // light up on every other path.
          const selected =
            entry.to === '/'
              ? pathname === '/'
              : pathname === entry.to || pathname.startsWith(entry.to + '/');
          return (
            <Box key={entry.to}>
              <ListItemButton
                component={RouterLink}
                to={entry.to}
                selected={selected}
                sx={{ mx: 1, borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: selected ? 'primary.main' : 'text.secondary' }}>
                  {entry.icon}
                </ListItemIcon>
                <ListItemText
                  primary={entry.label}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: selected ? 600 : 400 }}
                />
              </ListItemButton>
              {entry.slot}
            </Box>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: 'auto', py: 1 }}>
        {/* The sidebar's content is driven by the active top-level
            module from `AppHeader`. We render *only one* block at a
            time — there's no overlap between the modules, so a single
            switch is clearer than always rendering both and hiding one. */}
        {module === 'customer' && (
          // Tenant-scoped block — visually emphasised with a soft gradient
          // and a faint dot pattern so it reads as its own surface.
          // Switchers at the top (customer + LOB) keep the active scope
          // always one glance away.
          <Box
            sx={{
              mx: 1,
              mt: 0.5,
              pt: 1,
              pb: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.primary.main, 0.18),
              backgroundImage: (t) =>
                `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(
                  t.palette.primary.main,
                  0.03,
                )} 100%),` +
                `radial-gradient(${alpha(t.palette.primary.main, 0.08)} 1px, transparent 1px)`,
              backgroundSize: 'auto, 14px 14px',
              backgroundPosition: '0 0, 0 0',
              backgroundColor: 'background.paper',
              boxShadow: (t) => `inset 0 1px 0 ${alpha(t.palette.primary.main, 0.05)}`,
            }}
          >
            {/* Active customer selector — drives the JWT's tenantId via
                POST /auth/switch-tenant; on switch, TanStack Query's
                cache is cleared so every list re-fetches under the new
                tenant. */}
            <Box sx={{ px: 1.5, pb: 1 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Active customer
              </Typography>
              <TenantSwitcher fullWidth />
            </Box>

            {renderSection(customerSettings)}

            {/* Active line of business selector — real tree picker now,
                even though the JWT doesn't carry an LOB scope yet. The
                "+ New line of business" item at the bottom of the menu
                opens the same create dialog the LOB CRUD page uses. */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Active line of business
              </Typography>
              <LineOfBizSwitcher
                fullWidth
                value={activeLineOfBizId}
                onChange={handleLineOfBizChange}
              />
            </Box>

            {/* Greys out the whole "Line of business settings" group
                until a LOB is picked from the switcher above — the items
                are all LOB-scoped and pretending to navigate without a
                scope would just lead to mixed-up data. */}
            {renderSection(lineOfBizSettings, { disabled: activeLineOfBizId == null })}

            {/* "Report template settings" — title + dropdown rendered
                inline so the dropdown sits flush under the header,
                then `renderSection` handles the three sub-rows. */}
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="overline"
                sx={{ px: 2.5, color: 'text.secondary', display: 'block' }}
              >
                Active report template
              </Typography>
              <Box sx={{ px: 1.5, pt: 0.5, pb: 0.5 }}>
                <ReportTemplateSwitcher fullWidth lineOfBizId={activeLineOfBizId} />
              </Box>
              {renderSection(buildReportTemplateSubSections(selectedReportTemplateId), {
                // Two gates: no LOB (whole block is dead) or no template
                // resolved within the active LOB (sub-rows have no
                // target).
                disabled: activeLineOfBizId == null || selectedReportTemplateId == null,
              })}
            </Box>
          </Box>
        )}

        {module === 'customViews' && (
          // Custom Views block — same scoped surface as the customer
          // block: the tenant + LOB switchers pin the scope, then the
          // custom-view switcher picks (or creates) the dashboard shown
          // in the content area.
          <Box
            sx={{
              mx: 1,
              mt: 0.5,
              pt: 1,
              pb: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.primary.main, 0.18),
              backgroundImage: (t) =>
                `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(
                  t.palette.primary.main,
                  0.03,
                )} 100%),` +
                `radial-gradient(${alpha(t.palette.primary.main, 0.08)} 1px, transparent 1px)`,
              backgroundSize: 'auto, 14px 14px',
              backgroundPosition: '0 0, 0 0',
              backgroundColor: 'background.paper',
              boxShadow: (t) => `inset 0 1px 0 ${alpha(t.palette.primary.main, 0.05)}`,
            }}
          >
            <Box sx={{ px: 1.5, pb: 1 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Active customer
              </Typography>
              <TenantSwitcher fullWidth />
            </Box>

            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Active line of business
              </Typography>
              <LineOfBizSwitcher
                fullWidth
                value={activeLineOfBizId}
                onChange={handleLineOfBizChange}
              />
            </Box>

            {/* Active custom view selector — navigates to the picked
                view's canvas; the "+ New custom view" item at the bottom
                of the menu opens the create dialog. */}
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Active custom view
              </Typography>
              <CustomViewSwitcher fullWidth lineOfBizId={activeLineOfBizId} />
            </Box>
          </Box>
        )}

        {module === 'global' && (
          // Global block — plain background, no gradient. Lambda wrapper
          // around `renderSection` so `Array#map` doesn't pass the index
          // as the second arg (which would clash with the `opts` param).
          <Box>{globalSections.map((s) => renderSection(s))}</Box>
        )}

        {module === 'reports' && (
          <Box>
            {reportsSections.map((s) => renderSection(s))}
            <ReportsFilterPanel />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
